import type { Role, HalfDayMethod } from '@/types/Role';
import { formatCurrency } from '@/utils/formatters';

/** Whether this method stores its value in the halfDayMultiplier (percentage) field. */
export const isPercentageMethod = (m: HalfDayMethod | null | undefined): m is 'multiplier' | 'pct_discount' =>
  m === 'multiplier' || m === 'pct_discount';

/** Radio/button options for the half-day rate method selector (UI labels). */
export const HALF_DAY_METHOD_OPTIONS: { value: HalfDayMethod; label: string; description: string }[] = [
  { value: 'fixed', label: 'Fixed Rate', description: 'e.g. $450' },
  { value: 'multiplier', label: '% of Rate', description: 'e.g. 60%' },
  { value: 'pct_discount', label: '% Discount', description: 'e.g. 25% off' },
  { value: 'fixed_discount', label: '$ Discount', description: 'e.g. $200 off' },
];

/** Builds template variables for the half-day rate method help markdown. */
export const buildHalfDayHelpVars = (dayRate: number) => ({
  dayRate,
  pctResult: Math.round(dayRate * 0.6),
  pctDiscResult: Math.round(dayRate * 0.75),
  discResult: dayRate - 150,
});

/** Returns smart default half-day config based on the most recent configured role, or Percentage 60%. */
export const getHalfDayDefaults = (roles: Role[]): Pick<Role, 'halfDayMethod' | 'halfDayMultiplier' | 'halfDayFixedRate' | 'halfDayFixedDiscount'> => {
  const lastConfigured = roles
    .filter(r => r.billingStructure === 'day_rate' && r.allowHalfDay && r.halfDayMethod)
    .pop();
  return {
    halfDayMethod: lastConfigured?.halfDayMethod ?? 'fixed',
    halfDayMultiplier: lastConfigured?.halfDayMultiplier ?? null,
    halfDayFixedRate: lastConfigured?.halfDayFixedRate ?? null,
    halfDayFixedDiscount: lastConfigured?.halfDayFixedDiscount ?? null,
  };
};

/**
 * Format a calculated half-day rate from individual form state values.
 * Returns a formatted currency string, or null if inputs are insufficient.
 */
export const formatCalculatedHalfDayRate = (
  rate: number | null,
  method: HalfDayMethod | null,
  multiplier: number | null,
  fixedDiscount: number | null,
): string | null => {
  if (!rate) return null;
  if (method === 'multiplier' && multiplier)
    return formatCurrency(rate * multiplier);
  if (method === 'pct_discount' && multiplier)
    return formatCurrency(rate * (1 - multiplier));
  if (method === 'fixed_discount' && fixedDiscount)
    return formatCurrency(Math.max(0, rate - fixedDiscount));
  return null;
};

export const calculateHalfDayRate = (
  role: (Role & { isNew?: boolean }) | undefined,
): number | null => {
  if (!role || role.billingStructure !== 'day_rate' || !role.allowHalfDay) {
    return null;
  }

  const fullDayRate = role.rate || 0;

  const fixedRate = role.halfDayFixedRate;
  const fixedDiscount = role.halfDayFixedDiscount;

  if (isPercentageMethod(role.halfDayMethod) && typeof role.halfDayMultiplier === 'number') {
    const effectiveMultiplier = role.halfDayMethod === 'pct_discount'
      ? 1 - role.halfDayMultiplier
      : role.halfDayMultiplier;
    return fullDayRate * effectiveMultiplier;
  } else if (role.halfDayMethod === 'fixed' && typeof fixedRate === 'number' && !isNaN(fixedRate)) {
    return fixedRate;
  } else if (role.halfDayMethod === 'fixed_discount' && typeof fixedDiscount === 'number' && !isNaN(fixedDiscount)) {
    const result = fullDayRate - fixedDiscount;
    return result < 0 ? 0 : result;
  }

  return null;
};
